import {
  IonFooter,
  IonHeader,
  IonItem,
  IonLabel,
  IonPage,
  IonTitle,
  useIonViewWillEnter,
} from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { FormToggle } from "@/components/atoms/form-ion/toggle";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { PermissionExplainerModal } from "@/components/modal/app/permission-explainer";
import { OtherDevicesCard } from "@/components/molecules/notifications/other-devices-card";
import { PermissionDeniedCard } from "@/components/molecules/notifications/permission-denied-card";
import { Toolbar } from "@/components/toolbar";
import { useBoomerForm } from "@/form/boomer/hook";
import { api } from "@/lib/backend";
import { LocalNotifications } from "@/lib/capacitor/local-notifications";
import { chevronRightIcon } from "@/lib/icons/chevron-right.icon";
import { queryClient } from "@/lib/providers/query";
import { queryOptions_pushNotificationsPermissions } from "@/lib/providers/query/functions/@capacitor/push-notifications/permissions";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";
import { cleanupDeviceRegistration } from "@/lib/utils/notifications";
import { logger } from "@/logger";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";

const TOAST_KEY = "notification-preferences-update";

function NotificationTypeItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/50">
      <div className="text-gray-600 dark:text-gray-400">{icon}</div>
      <span className="text-sm text-txt-primary">{label}</span>
    </div>
  );
}

export function SettingsNotificationsPage() {
  const { t } = useTranslation();
  const statusBarHeight = useStatusBarHeight();
  const presentModal = useSelectorModalStore.use.present();

  const { data: permissionStatus, refetch: refetchPermissions } = useQuery(
    queryOptions_pushNotificationsPermissions(),
  );

  const { data: userDevices, refetch: refetchDevices } = useQuery({
    queryKey: ["notification", "devices"],
    queryFn: api.notification.getDevices,
    enabled: Capacitor.getPlatform() !== "web",
  });

  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(null);
  const [isNotificationEnabled, setIsNotificationEnabled] = useState(false);
  const [showPermissionExplainer, setShowPermissionExplainer] = useState(false);

  // Get current device identifier on mount
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") {
      return;
    }

    import("@/lib/capacitor/device").then(({ Device }) => {
      Device.getDeviceIdentifier().then(setCurrentDeviceId);
    });
  }, []);

  // Determine if THIS device is registered (not just if user has any devices)
  useEffect(() => {
    if (!currentDeviceId || !userDevices?.data?.devices) {
      setIsNotificationEnabled(false);
      return;
    }

    const thisDevice = userDevices.data.devices.find(
      (device) => device.deviceIdentifier === currentDeviceId,
    );

    const hasPermission = permissionStatus === "granted";
    setIsNotificationEnabled(!!thisDevice && hasPermission);
  }, [userDevices, permissionStatus, currentDeviceId]);

  const requestPermissionsMutation = useMutation({
    mutationKey: ["@capacitor/push-notifications", "request-permissions"],
    mutationFn: async () => {
      if (Capacitor.getPlatform() === "web") {
        return "unavailable";
      }
      const permissions = await PushNotifications.requestPermissions();
      return permissions ?? "unknown";
    },
    onSettled: (data) => {
      queryClient.setQueryData(["@capacitor/push-notifications", "check-permissions"], data);
      refetchPermissions();
    },
  });

  const updateNotificationPreferencesMutation = useMutation({
    mutationKey: ["notification-preferences", "update"],
    mutationFn: async (input: { notificationsEnabled: boolean }) => {
      if (Capacitor.getPlatform() === "web") {
        toast.error(t("settings.notifications.status.web.error"), { id: TOAST_KEY });
        return;
      }

      if (input.notificationsEnabled) {
        let currentPermission = permissionStatus;

        // Show explainer modal if permission not determined yet (first time)
        if (currentPermission !== "granted" && currentPermission !== "denied") {
          setShowPermissionExplainer(true);
          throw new Error("Permission explainer shown");
        }

        if (currentPermission !== "granted") {
          const result = await requestPermissionsMutation.mutateAsync();
          currentPermission = typeof result === "string" ? result : result.receive;
        }
        if (currentPermission === "granted") {
          await PushNotifications.register();
          return { success: true };
        } else if (currentPermission === "denied") {
          presentModal("pushNotificationsDenied");
          throw new Error("Permission denied");
        } else {
          throw new Error("Permission not granted");
        }
      } else {
        // Use cleanup utility to unregister both locally and from backend
        await cleanupDeviceRegistration();
        return { success: true };
      }
    },
    onMutate: () => {
      toast.loading(t("actions:notification-preferences.loading"), { id: TOAST_KEY });
    },
    onSuccess: () => {
      toast.success(t("actions:notification-preferences.success"), { id: TOAST_KEY });
      refetchDevices();
      refetchPermissions();
    },
    onError: (error) => {
      toast.error(t("actions:notification-preferences.error"), { id: TOAST_KEY });
      logger.error(error);
    },
  });

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues: {
      notificationsEnabled: isNotificationEnabled,
    },
    defaultState: {
      canSubmit: Capacitor.getPlatform() !== "web",
    },
    onSubmit: async ({ value }) => {
      await updateNotificationPreferencesMutation.mutateAsync(value);
    },
  });

  useEffect(() => {
    form.setFieldValue("notificationsEnabled", isNotificationEnabled);
  }, [isNotificationEnabled, form]);

  useIonViewWillEnter(() => {
    refetchPermissions();
    refetchDevices();
  });

  const isWeb = Capacitor.getPlatform() === "web";
  // const showTestButtons = isNotificationEnabled && permissionStatus === "granted";
  const SHOW_TEST_BUTTONS = false;

  return (
    <FormElement>
      <IonPage>
        <IonHeader>
          <Toolbar style={{ paddingTop: `calc(${statusBarHeight}px + 0.5rem)` }}>
            <Buttons slot="secondary">
              <BackButton defaultHref={Routes.Settings()} />
            </Buttons>
            <IonTitle>{t("routes:notifications.title")}</IonTitle>
          </Toolbar>
        </IonHeader>

        <Content>
          <div className="flex flex-1 flex-col gap-4 pt-4">
            {isWeb && (
              <div className="mx-4 rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {t("settings.notifications.status.web.error")}
                </p>
              </div>
            )}

            {/* Main Toggle Card */}
            <div className="mx-4">
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="dark:bg-brand-900/50 flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-100">
                    <svg
                      className="size-6 text-brand-600 dark:text-brand-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      {isNotificationEnabled ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                        />
                      ) : (
                        <>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                          />
                          <line
                            x1="4"
                            y1="4"
                            x2="20"
                            y2="20"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                          />
                        </>
                      )}
                    </svg>
                  </div>

                  {/* Text and Toggle */}
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <h3 className="text-base font-semibold text-txt-primary">
                        {t("settings.notifications.this-device.title")}
                      </h3>
                      {isNotificationEnabled && (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/50 dark:text-green-300">
                          {t("settings.notifications.this-device.status-active")}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-txt-secondary">
                      {isNotificationEnabled
                        ? t("settings.notifications.this-device.subtitle.enabled")
                        : permissionStatus === "denied"
                          ? t("settings.notifications.this-device.subtitle.permission-denied")
                          : currentDeviceId
                            ? t("settings.notifications.this-device.subtitle.not-registered")
                            : t("settings.notifications.this-device.subtitle.disabled")}
                    </p>
                    <div className="mt-3">
                      <form.Field
                        name="notificationsEnabled"
                        validators={{ onChange: z.coerce.boolean() }}
                        children={(field) => (
                          <FormToggle field={field} disabled={isWeb}>
                            {t("settings.notifications.toggle.description")}
                          </FormToggle>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Permission Denied Card */}
            {permissionStatus === "denied" && !isWeb && <PermissionDeniedCard />}

            {/* Other Devices Card */}
            {!isWeb && currentDeviceId && userDevices?.data?.devices && (
              <OtherDevicesCard
                devices={userDevices.data.devices}
                currentDeviceId={currentDeviceId}
              />
            )}

            {/* What you'll receive section */}
            {!isWeb && (
              <div className="mx-4">
                <h4 className="mb-3 text-sm font-semibold text-txt-primary">
                  {t("settings.notifications.what-you-receive.title")}
                </h4>
                <div className="space-y-2">
                  <NotificationTypeItem
                    icon={
                      <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                        />
                      </svg>
                    }
                    label={t("settings.notifications.what-you-receive.types.shift-assignments")}
                  />
                  <NotificationTypeItem
                    icon={
                      <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    }
                    label={t("settings.notifications.what-you-receive.types.schedule-changes")}
                  />
                  <NotificationTypeItem
                    icon={
                      <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                        />
                      </svg>
                    }
                    label={t("settings.notifications.what-you-receive.types.team-updates")}
                  />
                </div>
              </div>
            )}

            {SHOW_TEST_BUTTONS && (
              <div className="mt-8">
                <div className="mx-4 mb-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t("settings.notifications.testing.title")}
                  </p>
                </div>

                <IonItem
                  detail
                  detailIcon={chevronRightIcon}
                  className="cursor-pointer"
                  onClick={async () => {
                    await LocalNotifications.schedule({
                      id: Date.now(),
                      title: t("settings.notifications.testing.local.content-title"),
                      body: t("settings.notifications.testing.local.content-body"),
                    });
                  }}
                >
                  <IonLabel>{t("settings.notifications.testing.local.action")}</IonLabel>
                </IonItem>

                {/* <IonItem
                  detail
                  detailIcon={chevronRightIcon}
                  className="cursor-pointer"
                  onClick={async () => {
                    try {
                      await api.notification.sendMessage({
                        title: t("settings.notifications.testing.push.content-title"),
                        body: t("settings.notifications.testing.push.content-body"),
                      });
                      toast.success(t("settings.notifications.testing.push.success"));
                    } catch (error) {
                      toast.error(t("settings.notifications.testing.push.error"));
                      logger.error(error);
                    }
                  }}
                >
                  <IonLabel>{t("settings.notifications.testing.push.action")}</IonLabel>
                </IonItem> */}
              </div>
            )}
          </div>
        </Content>

        <IonFooter className="ion-pb-[var(--safe-area-inset-bottom)]">
          <Toolbar>
            <form.SubmitButton />
          </Toolbar>
        </IonFooter>

        {/* Permission Explainer Modal */}
        <PermissionExplainerModal
          isOpen={showPermissionExplainer}
          onConfirm={async () => {
            setShowPermissionExplainer(false);
            // Request permission after user confirms
            const result = await requestPermissionsMutation.mutateAsync();
            const currentPermission = typeof result === "string" ? result : result.receive;

            if (currentPermission === "granted") {
              await PushNotifications.register();
              toast.success(t("notifications.enabled-success", "Notifications enabled"));
              refetchDevices();
              refetchPermissions();
            } else if (currentPermission === "denied") {
              presentModal("pushNotificationsDenied");
            }
          }}
          onDismiss={() => {
            setShowPermissionExplainer(false);
            // Reset form state
            form.reset();
          }}
        />
      </IonPage>
    </FormElement>
  );
}
